import { inject } from '@angular/core';
import { MetaDefinition } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { ISbStoryData } from '@storyblok/js';
import { map } from 'rxjs';
import { getMetaDescriptionTags, getMetaImageTags, getMetaTitleTags, noIndexMetaTag } from '../lib/meta-helpers';
import { PICFLOW_CONFIG } from '../picflow-config';
import { StoryblokHelperService } from '../services/storyblok-helper.service';
import { customRenderText, fixHtml, removeHtml } from './custom-render-text';
import { convertToOgImageUrl } from './lib/meta-helpers';

export const storyblokPageMetaResolver: (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  resolver: any
) => ResolveFn<unknown> = resolver => route => {
  const helper = inject(StoryblokHelperService);
  const config = inject(PICFLOW_CONFIG);

  const getTitle = (story: ISbStoryData): string => {
    if (story.content.metaTitle) {
      return story.content.metaTitle;
    }
    // find the first Title block in the body and return its title
    const titleBlock = helper.getBlocks(story.content.body, ['Title'], true).find(b => b?.title);
    if (titleBlock) {
      return titleBlock.title;
    }

    // otherwise return the story name as a backup
    return story.name;
  };

  const getDescription = (story: ISbStoryData): string => {
    if (story.content.metaDescription) return story.content.metaDescription;
    const block = helper.getBlocks(story.content.body, ['Text'], true)[0];
    if (!block?.content) return;
    return removeHtml(fixHtml(customRenderText(block.content)));
  };

  const getImageUrl = (story: ISbStoryData): string => {
    if (story.content.keyVisual?.filename) {
      return story.content.keyVisual.filename;
    }
  };

  return resolver(route).pipe(
    map((story: ISbStoryData) => {
      if (!story) return;
      const tags: MetaDefinition[] = [];

      const title = getTitle(story);
      const description = getDescription(story);
      const imageUrl = getImageUrl(story);

      tags.push(...getMetaTitleTags(title));
      tags.push(...getMetaDescriptionTags(description));

      if (imageUrl) {
        tags.push(...getMetaImageTags(convertToOgImageUrl(imageUrl, config.storyblokUrl)));
      }

      if (story.content.noIndex) {
        tags.push(noIndexMetaTag);
      }

      const pageBackground = story.content?.background;
      const hasHomeTeaser = story.content?.body?.[0]?.component === 'HomeTeaser';
      if (pageBackground?.includes('dark')) {
        tags.push({ property: 'theme-color', content: '#111319' });
      } else if (pageBackground?.includes('black') || hasHomeTeaser) {
        tags.push({ property: 'theme-color', content: '#000000' });
      }

      return { title, tags };
    })
  );
};
